import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Input,
  Label,
  List,
  ListItem,
  UniversalSelect,
  Well,
} from '@feathr/components';
import { objectId } from '@feathr/hooks';

import type { IAdGroupKeyword } from '../AdStep.types';
import { DEFAULT_KEYWORD_MATCH_TYPE } from '../AdStep.types';
import KeywordChip from './KeywordChip';
import type { IMatchTypeOption } from './KeywordFields.utils';
import { getKeywordMessages, getMatchTypeOptions } from './KeywordFields.utils';

import * as styles from './AdGroupEditor.css';

interface IKeywordFieldsProps {
  keywords: IAdGroupKeyword[];
  /** Messages for every keyword that has any, keyed by keyword key. */
  keywordErrors: Record<string, string[]>;
  onChange: (keywords: IAdGroupKeyword[]) => void;
  disabled?: boolean;
}

/**
 * The positive keywords for one ad group: existing keywords as removable chips,
 * plus a type-and-add row with a match type picker. Adding works from the button
 * or by pressing Enter in the text field.
 *
 * @param props - {@link IKeywordFieldsProps}.
 */
function KeywordFields({
  keywords,
  keywordErrors,
  onChange,
  disabled = false,
}: Readonly<IKeywordFieldsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [draft, setDraft] = useState('');
  const [matchType, setMatchType] = useState(DEFAULT_KEYWORD_MATCH_TYPE);

  const matchTypeOptions = getMatchTypeOptions(t);
  const selectedMatchType = matchTypeOptions.find((option) => option.id === matchType);
  const messages = getKeywordMessages(keywords, keywordErrors);

  function handleAdd(): void {
    const text = draft.trim();
    if (!text) {
      return;
    }
    onChange([...keywords, { key: objectId(), text, matchType }]);
    setDraft('');
  }

  function handleRemove(index: number): void {
    onChange(keywords.filter((_, i) => i !== index));
  }

  function handleDraftChange(value?: string): void {
    setDraft(value ?? '');
  }

  function handleMatchTypeChange(option: IMatchTypeOption): void {
    setMatchType(option.id);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  }

  return (
    <>
      <Label weight={'medium'}>{t('Keywords')}</Label>
      <Well layout={'vertical'} theme={'white'} width={'full'}>
        {!!keywords.length && (
          // Same chip-row gap the Location step uses for its LocationTags.
          <Flex gap={theme.spacing.half} wrap={'wrap'}>
            {keywords.map((keyword, index) => (
              <KeywordChip
                disabled={disabled}
                hasError={!!keywordErrors[keyword.key]?.length}
                index={index}
                key={keyword.key}
                keyword={keyword}
                onRemove={handleRemove}
                showMatchType={true}
              />
            ))}
          </Flex>
        )}
        {/* A chip has nowhere to put a message, so the ones marked in red are named here. */}
        {!!messages.length && (
          <Alert
            className={styles.alertInWell}
            title={t('Fix the keywords marked in red.')}
            type={'danger'}
          >
            <List>
              {messages.map((message) => (
                <ListItem key={message}>{message}</ListItem>
              ))}
            </List>
          </Alert>
        )}
        <Flex align={'flex-end'} className={styles.addRow} wrap={'wrap'}>
          <Input
            disabled={disabled}
            label={t('Add a keyword')}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            placeholder={t('Keyword or phrase')}
            type={'text'}
            value={draft}
            wrapperClassName={styles.grow}
          />
          <UniversalSelect<IMatchTypeOption>
            disabled={disabled}
            label={t('Match type')}
            name={'keyword-match-type'}
            onSelectSingle={handleMatchTypeChange}
            options={matchTypeOptions}
            value={selectedMatchType}
            wrapperClassName={styles.matchType}
          />
          <Button
            disabled={disabled || !draft.trim()}
            name={'add-keyword'}
            onClick={handleAdd}
            prefix={<FontAwesomeIcon icon={faPlus} />}
          >
            {t('Add keyword')}
          </Button>
        </Flex>
      </Well>
    </>
  );
}

export default KeywordFields;
